import React from 'react';

import { useCompany } from 'src/app/Company.store';
import { cn } from '@/lib/utils';

type HomeSection = {
  title?: string;
  description?: string;
  link_label?: string;
  link_url?: string;
  background_image?: string;
  background_color?: string;
};

const HomeContent = () => {
  const { data: company } = useCompany();
  const homePageSettings = company.page_description_settings?.find(
    (setting) => setting?.page === 'home'
  );

  const sections: HomeSection[] | undefined = homePageSettings?.sections;

  if (!sections?.length) {
    return null;
  }

  // With 2+ cards, the mobile layout is a swipeable horizontal carousel: native
  // scroll-snap, the next card peeking at the edge, no controls. `-mx-6` bleeds
  // the scroller full-width to the viewport edges (cancelling the page wrapper's
  // `px-6`), so cards clip at the viewport edge when dragged. `px-6` keeps the
  // first/last cards inset by a gutter, and `scroll-pl-6` makes scroll-snap honor
  // that left gutter (without it, snap yanks the first card flush to the edge).
  // `overscroll-x-contain` keeps the horizontal swipe from chaining to the
  // vertical page scroll. At `md` it reverts to the two-column grid. A single
  // card stays a normal full-width grid (a lone peeking card looks truncated).
  const isCarousel = sections.length > 1;

  return (
    <div
      className={cn(
        isCarousel
          ? '-mx-6 flex snap-x snap-mandatory scroll-pl-6 gap-6 overflow-x-auto overscroll-x-contain px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0'
          : 'grid grid-cols-1 gap-6 md:grid-cols-2'
      )}
    >
      {sections.map((section, index) => {
        const hasLink = !!section.link_url;
        // Tenants supply either an image or a color. Image wins (with a dark
        // gradient overlay so the white text stays readable on any photo);
        // color falls back to the WasteHero brand navy. Backend colors are
        // arbitrary hex strings, so they ride on the inline style.
        const style: React.CSSProperties = section.background_image
          ? {
              backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url("${section.background_image}")`,
              backgroundColor: section.background_color || 'var(--brand-navy)',
            }
          : {
              backgroundColor: section.background_color || 'var(--brand-navy)',
            };

        return (
          <button
            type="button"
            key={index}
            onClick={
              hasLink
                ? () => window.open(section.link_url, '_blank')
                : undefined
            }
            disabled={!hasLink}
            style={style}
            className={cn(
              'flex flex-col gap-6 rounded-2xl bg-cover bg-center bg-no-repeat p-8 text-left text-white transition-transform',
              isCarousel &&
                'shrink-0 basis-[85%] snap-start snap-always md:basis-auto md:shrink',
              hasLink && 'cursor-pointer hover:-translate-y-0.5',
              !hasLink && 'cursor-default'
            )}
          >
            <h3 className="line-clamp-2 text-2xl font-semibold leading-tight tracking-tight">
              {section.title}
            </h3>
            <p className="line-clamp-6 text-base leading-normal">
              {section.description}
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default HomeContent;
